import { IdmmEntry } from "@org-avp/avp-avengers-idmm-service-api";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useState } from "react";
import { idmmApi } from "../apis/idmm-service-api";
import { pollingInterval } from "../polling/pollingInterval";

interface IdmmHelper {
  idmmEntries: IdmmEntry[] | undefined;
  refetch: () => void;
}

export const useIdmmHelper = (tmmId: string): IdmmHelper => {
  const { data: getAllIdmmEntriesResponse, refetch } = idmmApi.useGetAllIdmmEntriesQuery(tmmId);
  const idmmEntries = useMemo(() => getAllIdmmEntriesResponse?.idmmEntries, [getAllIdmmEntriesResponse?.idmmEntries]);
  const initialFetchTimestamp = useMemo(
    () => getAllIdmmEntriesResponse?.lastModified,
    [getAllIdmmEntriesResponse?.lastModified],
  );

  // use initial fetch timestamp of idmmEntries as latestUpdateTimestamp
  const [latestUpdateTimestamp, setLatestUpdateTimestamp] = useState<string>();
  useEffect(() => {
    if (latestUpdateTimestamp === undefined && idmmEntries && initialFetchTimestamp) {
      setLatestUpdateTimestamp(new Date(initialFetchTimestamp).toISOString());
    }
  }, [idmmEntries, initialFetchTimestamp, latestUpdateTimestamp]);

  // poll IdmmUpdate: updates idmmEntries of "getAllIdmmEntries" query via API implementation
  const { currentData: idmmUpdate } = idmmApi.useGetIdmmUpdateQuery(latestUpdateTimestamp ? [tmmId, latestUpdateTimestamp] : skipToken, {
    pollingInterval: pollingInterval.tmm.getIdmmUpdate,
  });

  // update the "latestUpdate" timestamp when new data is fetched
  useEffect(() => {
    if (idmmUpdate) {
      setLatestUpdateTimestamp(idmmUpdate.lastModifiedAfter);
    }
  }, [idmmUpdate]);

  return useMemo(() => ({ idmmEntries, refetch }), [idmmEntries, refetch]);
};
